import React from 'react';
import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

/**
 * KPI Card Component - For displaying key metrics
 * Used in the top row of dashboard for Revenue, Orders, Chat, Response Time
 */
export const KPICard = ({ 
  title, 
  value, 
  icon: Icon, 
  trend, 
  trendValue,
  trendLabel,
  color = 'blue',
  compact = false,
  suffix = '',
  prefix = ''
}) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600',
    amber: 'from-amber-500 to-amber-600',
    rose: 'from-rose-500 to-rose-600'
  };

  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUp className="w-3 h-3 flex-shrink-0" />;
    if (trend === 'down') return <TrendingDown className="w-3 h-3 flex-shrink-0" />;
    return <Minus className="w-3 h-3 flex-shrink-0" />;
  };

  const getTrendColor = () => {
    if (trend === 'up') return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20';
    if (trend === 'down') return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20';
    return 'text-slate-500 bg-slate-50 dark:bg-zinc-800';
  };

  return (
    <div className={cn(
      'bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800',
      'shadow-sm hover:shadow-md transition-all duration-200',
      compact ? 'p-3' : 'p-4'
    )}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] sm:text-xs text-slate-500 dark:text-zinc-400 font-medium uppercase tracking-wide">
            {title}
          </p>
          <p className={cn(
            'font-bold text-slate-900 dark:text-zinc-50 mt-1 whitespace-nowrap',
            compact ? 'text-xs sm:text-lg' : 'text-xs sm:text-xl lg:text-2xl'
          )}>
            {prefix}{value}{suffix}
          </p>
          
          {/* Trend indicator - Always in 1 line */}
          {(trend || trendValue) && (
            <div className="flex items-center gap-1 mt-2 flex-nowrap">
              <span className={cn(
                'inline-flex items-center gap-0.5 px-1 sm:px-1.5 py-0.5 rounded text-[9px] sm:text-[10px] font-medium whitespace-nowrap flex-shrink-0',
                getTrendColor()
              )}>
                {getTrendIcon()}
                <span className="truncate max-w-[60px] sm:max-w-none">{trendValue}</span>
              </span>
              {trendLabel && (
                <span className="text-[9px] sm:text-[10px] text-slate-500 whitespace-nowrap flex-shrink-0 dark:text-zinc-400">{trendLabel}</span>
              )}
            </div>
          )}
        </div>
        
        {/* Icon */}
        {Icon && (
          <div className={cn(
            'rounded-lg bg-gradient-to-br flex items-center justify-center flex-shrink-0',
            colorClasses[color] || colorClasses.blue,
            compact ? 'w-8 h-8 sm:w-9 sm:h-9' : 'w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12'
          )}>
            <Icon className={cn('text-white', compact ? 'w-4 h-4' : 'w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6')} />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Mini KPI - Compact inline version for grouped displays
 */
export const MiniKPI = ({ title, value, icon: Icon, color = 'blue' }) => {
  const colorClasses = {
    blue: 'from-blue-400 to-blue-600',
    green: 'from-green-400 to-green-600',
    purple: 'from-purple-400 to-purple-600',
    amber: 'from-amber-400 to-amber-600'
  };

  return (
    <div className="flex items-center gap-3">
      <div className={cn(
        'w-9 h-9 rounded-lg bg-gradient-to-br flex items-center justify-center',
        colorClasses[color] || colorClasses.blue
      )}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div>
        <p className="text-[10px] text-slate-500 dark:text-zinc-400">{title}</p>
        <p className="text-sm font-bold text-slate-900 dark:text-zinc-50">{value}</p>
      </div>
    </div>
  );
};

export default KPICard;
